// Native OS notifications for the Electron desktop shell.
// Forwards socket `push:relay` messages to the main process, which shows a
// native notification only when the window is unfocused/minimized/hidden.
// No-op outside the desktop shell (window.doitifyDesktop is injected by the
// Electron preload).
import { getSocketUserChat } from "./APIFuncs/socketUserChat";

const SEEN_TTL_MS = 10 * 60 * 1000;
const seen = new Map();

function pruneSeen() {
  const now = Date.now();
  for (const [key, ts] of seen) {
    if (now - ts > SEEN_TTL_MS) seen.delete(key);
  }
}

function isDuplicate(messageId) {
  const id = String(messageId ?? "").trim();
  if (!id) return false;
  pruneSeen();
  return seen.has(id);
}

function markSeen(messageId) {
  const id = String(messageId ?? "").trim();
  if (!id) return;
  pruneSeen();
  seen.set(id, Date.now());
}

let initialized = false;

export function initDesktopNativeNotifications() {
  if (typeof window === "undefined" || initialized) return;
  const api = window.doitifyDesktop;
  if (!api?.notifications) return;
  initialized = true;

  // Click on the native notification → open its target in the app.
  api.notifications.onClicked((payload) => {
    const link = String(payload?.link ?? "").trim();
    if (link) window.location.href = link;
  });

  let detach = () => {};

  const tryAttach = () => {
    detach();
    const socket = getSocketUserChat();
    if (!socket) return false;
    const onRelay = (relay) => {
      try {
        const mid = String(relay?.messageId ?? "").trim();
        if (mid && isDuplicate(mid)) return;
        if (mid) markSeen(mid);
        void api.notifications.show({
          messageId: mid,
          title: String(relay?.title ?? "Doitify"),
          body: String(relay?.body ?? ""),
          link: String(relay?.data?.link ?? ""),
        });
      } catch {
        /* ignore relay forwarding errors */
      }
    };
    socket.on("push:relay", onRelay);
    detach = () => socket.off("push:relay", onRelay);
    return true;
  };

  if (tryAttach()) return;
  const interval = setInterval(() => {
    if (tryAttach()) clearInterval(interval);
  }, 120);
  setTimeout(() => clearInterval(interval), 12000);
}
